<template>
  <div>
    <v-row class="ml-0 my-auto">
      <p class="font-weight-bold mb-0">Tasks</p>
      <v-progress-circular
        indeterminate
        :size="25"
        v-show="isSavingTask"
        class="ml-5"
        color="primary"
      ></v-progress-circular>
    </v-row>
    <div v-for="(task, t) in event.tasks" :key="`task${t}`" class="my-5">
      <v-row>
        <!-- Task Header -->
        <v-col sm="10">
          <p>Task #{{ t + 1 }}</p>
        </v-col>
        <!-- Generate Task Excel -->
        <v-col sm="1">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-large
                icon
                @click="generateExcel(task)"
                color="primary"
                :loading="isTaskExcelLoading"
                :disabled="isTaskExcelLoading"
              >
                <download-excel
                  :title="title"
                  :data="report_excel"
                  :fields="excel_fields"
                  worksheet="Task Report"
                  name="Task-Report.xls"
                >
                  <v-icon>mdi-microsoft-excel</v-icon>
                </download-excel>
              </v-btn>
            </template>
            <span>Download Excel </span>
          </v-tooltip>
        </v-col>
        <!-- /Generate Task Excel -->
        <v-col sm="1" v-if="userPermissions.find((up) => up == 'DELETE_TASK')">
          <v-dialog
            persistent
            v-model="isDeleteTaskDialogs[event.id + t]"
            max-width="600px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn fab x-small dark color="red" class="mt-2">
                <v-icon dark v-bind="attrs" v-on="on"> mdi-close-thick </v-icon>
              </v-btn>
            </template>

            <v-card elevation="10">
              <v-card-title>
                <v-icon large color="primary"> mdi-information-outline </v-icon>
                &nbsp; Do you want to remove the task?
              </v-card-title>
              <v-card-text>
                <v-btn color="primary" @click.stop="deleteTask(task)">
                  Yes
                </v-btn>
                &nbsp;
                <v-btn color="red" dark @click.stop="closeTaskDialogs">
                  No
                </v-btn>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-col>
        <!--/ Task Header -->
      </v-row>
      <v-row>
        <!-- row -->
        <v-col sm="5">
          <v-text-field
            v-model="task.name"
            label="Task Name *"
            @keypress.enter="save_task(task)"
            prepend-inner-icon="mdi-calendar-check"
            :error-messages="
              task.name == '' ? 'this name field is required' : ''
            "
          ></v-text-field>
        </v-col>
        <v-col sm="1">
          <v-tooltip top v-show="task.id">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mt-2"
                icon
                v-bind="attrs"
                x-large
                v-on="on"
                @click="show_task_details(task.id)"
              >
                <v-badge
                  overlap
                  color="pink"
                  right
                  :content="task.task_comments.length"
                >
                  <v-icon>mdi-comment-plus-outline </v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span
              >View
              {{ task.task_comments?.length || 0 }}
              Task Comments
            </span>
          </v-tooltip>
        </v-col>
        <v-col sm="6" v-if="userPermissions.find((up) => up == 'ADD_ASSIGNEE')">
          <v-autocomplete
            class="mt-2"
            v-model="task.Assignees"
            :disabled="isUpdating"
            :items="userItems"
            chips
            @input="save_task(task)"
            dense
            color="blue-grey lighten-2"
            label="Select Assignees"
            item-text="name"
            item-value="value"
            multiple
          >
            <template v-slot:selection="data">
              <v-chip
                v-bind="data.attrs"
                :input-value="data.selected"
                close
                @click="data.select"
                @click:close="remove_assignee(data.item, event.tasks[t])"
              >
                {{ data.item.initials }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <template>
                <v-list-item-content>
                  <v-list-item-title
                    v-html="data.item.name"
                  ></v-list-item-title>
                </v-list-item-content>
              </template>
            </template>
          </v-autocomplete>
        </v-col>
        <!-- /row-->
      </v-row>
      <v-row>
        <!-- row-->
        <v-col sm="3">
          <v-select
            v-model="task.status"
            :items="statusItems"
            chips
            @input="save_task(task)"
            item-color="red"
            label="Status"
            class="mt-2"
            dense
          >
            <template v-slot:selection="{ item }">
              <v-chip dark v-if="item == 'Working on it'" color="orange">{{
                item
              }}</v-chip>
              <v-chip dark v-if="item == 'Stuck'" color="red">{{
                item
              }}</v-chip>
              <v-chip dark v-if="item == 'Done'" color="green">{{
                item
              }}</v-chip>
            </template>
          </v-select>
        </v-col>
        <v-col sm="3" class="wrap">
          <input
            v-if="!task.document_path"
            type="file"
            :id="`document_${task.id}`"
            name="document"
            :ref="`document_${task.id}`"
            @change="save_task(task, true)"
          />
          <span v-else>
            <v-tooltip top v-show="task.id">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mt-2"
                  icon
                  v-bind="attrs"
                  x-large
                  v-on="on"
                  target="_blank"
                  :href="`${mediaUrl + task.document_path}`"
                >
                  <v-icon>mdi-attachment </v-icon>
                </v-btn>
              </template>
              <span>View Task Document </span>
            </v-tooltip>
            <v-tooltip bottom v-show="task.id">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  x-small
                  v-on="on"
                  class="reset_image my-1"
                  color="red"
                  dark
                  @click="(task.document_path = null), save_task(task)"
                  >Remove</v-btn
                >
              </template>
              <span>Remove Task Document </span>
            </v-tooltip>
          </span>
        </v-col>
        <v-col sm="3">
          <v-text-field
            readonly
            v-model="task.start_date"
            label="Start Date"
          ></v-text-field>
        </v-col>
        <v-col sm="3">
          <v-menu
            v-model="task.end_date_menu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="task.end_date"
                label="Follow Up Date"
                :id="`end_S_date_${t}`"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                :error-messages="
                  errors.end_date ? 'Follow up Date field is reqiuired' : ''
                "
              ></v-text-field>
            </template>

            <v-date-picker
              :id="`end_date_${t}`"
              @input="save_task(task)"
              v-model="task.end_date"
              @change="task.end_date_menu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <!-- /row -->
      </v-row>
      <v-divider class="my-5"></v-divider>
    </div>
    <v-btn
      outlined
      small
      class="my-5"
      color="primary"
      @click="addEmptyTask"
      v-if="userPermissions.find((up) => up == 'CREATE_TASK')"
    >
      <v-icon dark> mdi-plus </v-icon> Add Task
    </v-btn>
    <!-- Task Log Pop UP  -->
    <v-dialog
      v-model="show_details"
      transition="dialog-top-transition"
      max-width="800px"
    >
      <v-card elevation="2">
        <v-card-title>
          <span>
            <h2>{{ task_details.name }}</h2>
          </span>
          <v-spacer></v-spacer>
          <v-btn icon @click="show_details = false">
            <v-icon>mdi-window-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mt-5">
          <template>
            <v-tabs v-model="tab" align-with-title>
              <v-tab> Update </v-tab>
              <v-tab> Activity Log </v-tab>
            </v-tabs>
          </template>
          <v-tabs-items v-model="tab">
            <!-- Update remarks Tab -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <v-text-field
                        dense
                        flat
                        outlined
                        v-if="show_editor == false"
                        @click="show_editor = true"
                      ></v-text-field>
                      <div v-if="show_editor">
                        <v-row>
                          <v-col>
                            <vue-editor
                              v-model="task_details.task_comment"
                              @keypress.enter="save_task(task_details)"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-spacer> </v-spacer>
                          <v-col sm="3" class="px-0">
                            <v-btn small @click="show_editor = false"
                              >cancel</v-btn
                            >
                            <v-btn
                              class="ml-2"
                              dark
                              color="indigo"
                              small
                              @click="save_task(task_details)"
                              >Submit</v-btn
                            >
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col
                      md="12"
                      v-for="(task_comment, tc) in task_details.task_comments"
                      :key="`task_comment${tc}`"
                    >
                      <v-card class="mx-auto" outlined>
                        <v-card-title>
                          <span v-if="task_comment.user"
                            >{{ task_comment.user.first_name }}
                            {{ task_comment.user.last_name }}</span
                          >
                          <v-spacer></v-spacer>
                          <v-menu bottom left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn icon v-bind="attrs" v-on="on">
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item>
                                <v-list-item-title>Edit</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-card-title>
                        <v-list-item three-line>
                          <v-list-item-content>
                            <v-list-item-subtitle v-html="task_comment.comment"
                              >Greyhound divisely hello coldly
                              fonwderfully</v-list-item-subtitle
                            >
                          </v-list-item-content>
                        </v-list-item>
                        <v-card-actions>
                          <v-btn outlined text>
                            <v-icon>mdi-thumb-up-outline</v-icon>
                            Like
                          </v-btn>
                          <v-btn outlined text>
                            <v-icon>mdi-reply-outline</v-icon>
                            Reply
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
            <!-- /Update remarks Tab -->
            <!-- Activity log -->
            <v-tab-item>
              <v-card flat>
                <v-simple-table fixed-header>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Sr No</th>
                        <th class="text-left">Task</th>
                        <th class="text-left">Log</th>
                        <th class="text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(task_log, i) in task_details.task_logs"
                        :key="`task_log${i}`"
                      >
                        <td>{{ i + 1 }}</td>
                        <td>{{ task_details.name }}</td>
                        <td>{{ task_log.log }}</td>
                        <td>{{ task_log.created_at | ddmmyyyy }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- / Task Log Pop UP -->
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
export default {
  data() {
    return {
      tab: null,
      isUpdating: false,
      isSavingTask: false,
      show_details: false,
      show_editor: false,
      isTaskExcelLoading: false,
      isDeleteTaskDialogs: [],
      statusItems: ["Working on it", "Stuck", "Done"],
      task_details: [],
      editorSettings: {
        modules: {
          imageDrop: true,
          imageResize: {
            displaySize: true,
            modules: ["Resize", "DisplaySize", "Toolbar"],
          },
        },
      },
      excel_fields: {
        "Sr No": "sr_no",
        Name: "name",
        Status: "status",
        "Start Date": "start_date",
        "Follow Up Date": "end_date",
        Assignees: "assignees",
      },
      report_excel: [],
      title: "Task Report",
    };
  },
  props: ["form", "event", "userItems"],
  methods: {
    // Add an Empty task
    addEmptyTask() {
      let task = {
        event_id: this.event.id,
        name: "",
        start_date: moment(this.event.start_date).format("Y-MM-DD"),
        task_assignees: [],
        task_logs: [],
        Assignees: [],
        end_date_menu: false,
        status: "",
        document_path: "",
      };
      this.event.tasks.push(task);
    },
    // Delete task
    deleteTask(task) {
      // remove from Array
      this.event.tasks.splice(this.event.tasks.indexOf(task), 1);
      // change task is_deleted to true
      task.is_deleted = true;
      // Save task
      this.save_task(task);
      this.closeTaskDialogs();
    },
    closeTaskDialogs() {
      this.isDeleteTaskDialogs = [];
    },
    remove_assignee(item, task) {
      // remove Assignee from Array
      const index = task.Assignees.indexOf(item.id);
      if (index >= 0) task.Assignees.splice(index, 1);
      // Save task
      this.save_task(task);
    },
    // Trigger task Details Pop up
    async show_task_details(id) {
      this.task_details = [];
      this.task_details = this.event.tasks.filter((task) => task.id == id)[0];
      if (this.task_details) {
        this.show_details = true;
      }
    },
    async save_task(task, upload = 0) {
      try {
        console.log("Save_task");
        console.log(task);
        let task_assignees = [];
        if (task.Assignees) {
          task.Assignees.forEach((assignee_id) => {
            let task_assignee = task.task_assignees.find(
              (ta) => ta.assignee_id == assignee_id
            );
            if (task_assignee) {
              task_assignees.push(task_assignee);
            } else {
              let task_assignee = {
                task_id: task.id,
                assignee_id: assignee_id,
              };
              task_assignees.push(task_assignee);
            }
          });
          task.task_assignees = task_assignees;
        }

        if (task.task_comment) {
          let comment = {
            comment: task.task_comment,
            user_id: this.user.id,
          };
          task.task_comments.push(comment);
          this.show_editor = false;
        }
        this.isSavingTask = true;
        let response = await axios.post("/tasks", task);
        if (upload == true) {
          this.handleFileUpload(task);
        }
        if (response) {
          // Reassign response from DB to task[]
          let task_response = response.data.data;
          task.id = task_response.id;
          task.task_assignees = task_response.task_assignees;
          task.Assignees = [];
          task.task_assignees.forEach((assignee) => {
            task.Assignees.push(assignee.assignee_id);
          });
          task.task_logs = task_response.task_logs;
          task.task_comments = task_response.task_comments;
          task.task_comment = "";
          task.start_date = task_response.start_date;
        }
        this.isSavingTask = false;
      } catch (e) {
        console.log(e);
        this.isSavingTask = false;
      }
    },
    async handleFileUpload(task) {
      this.isLoading = true;
      let name = "document_" + task.id;
      let attachment = this.$refs[name][0]?.files[0];
      const taskId = task.id;
      let formData = new FormData();
      formData.append("taskId", taskId);
      formData.append("document_path", attachment);
      let response = await axios
        .post("upload_task_document", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .catch(function () {
          console.log("FAILURE!!");
        });
      this.isLoading = false;
      let task_response = response.data.data;
      task.document_path = task_response.document_path;
      console.log("Upload File for task id" + task.id);
    },
    generateExcel(task) {
      this.isTaskExcelLoading = true;
      console.log(task);
      this.exportToExcel(task);

      this.isTaskExcelLoading = false;
    },
    exportToExcel(task) {
      this.title =
        "Event Name: " +
        this.event.name +
        " Start Date & Time :" +
        moment(this.event.start_date).format("Y-MM-DD H:mm");
      this.report_excel = [];
      let sr_no = 1;
      let task_report = {
        sr_no: sr_no,
        name: task.name,
        status: task.status,
        start_date: task.start_date,
        end_date: task.end_date,
      };

      if (task.assignees) {
        task_report["assignees"] = task.assignees
          .map((assignee) => assignee.first_name + " " + assignee.last_name)
          .join(", ");
      } else {
        let assignees_array = [];
        task.task_assignees.forEach((assignee) => {
          let user = this.userItems.find(
            (user) => user.id == assignee.assignee_id
          );
          assignees_array.push(user.name);
          console.log(user);
        });
        task_report["assignees"] = assignees_array.join(", ");
      }
      this.report_excel.push(task_report);
      sr_no++;
    },
  },
};
</script>
<style scoped>
.wrap {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
</style>
